import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Empty } from "./AdminLeads";
import { toast } from "sonner";
import { Trash2, Pencil, Plus } from "lucide-react";

const EMPTY = { question: "", answer: "", order: 0 };

export function AdminFaqs() {
  const qc = useQueryClient();
  const { data: faqs = [], isLoading } = useQuery({ queryKey: ["admin-faqs"], queryFn: () => api.get("/faqs").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm({ ...form, [k]: k === "order" ? Number(e.target.value) : e.target.value });

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: faqs.length }); setOpen(true); };
  const openEdit = (f) => { setEditing(f.id); setForm(f); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/faqs/${editing}`, form);
      else await api.post("/faqs", form);
      qc.invalidateQueries({ queryKey: ["admin-faqs"] });
      qc.invalidateQueries({ queryKey: ["faqs"] });
      toast.success(editing ? "FAQ updated" : "FAQ added");
      setOpen(false);
    } catch (err) { toast.error("Could not save FAQ"); }
  };
  const remove = async (id) => {
    await api.delete(`/faqs/${id}`);
    qc.invalidateQueries({ queryKey: ["admin-faqs"] });
    qc.invalidateQueries({ queryKey: ["faqs"] });
    toast.success("FAQ deleted");
  };

  return (
    <div data-testid="admin-faqs">
      <div className="mb-6 flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="faq-new-btn" className="rounded-none bg-cyan font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90">
              <Plus className="mr-2 h-4 w-4" /> New FAQ
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl border-border bg-card">
            <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold uppercase">{editing ? "Edit" : "New"} FAQ</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <Input required data-testid="faq-question" className="tool-input rounded-none" placeholder="Question" value={form.question} onChange={set("question")} />
              <Textarea required data-testid="faq-answer" className="tool-input min-h-[140px] rounded-none" placeholder="Answer" value={form.answer} onChange={set("answer")} />
              <Input type="number" data-testid="faq-order" className="tool-input rounded-none" placeholder="Order" value={form.order} onChange={set("order")} />
              <Button type="submit" data-testid="faq-save" className="w-full rounded-none bg-cyan font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90">{editing ? "Update" : "Add"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? <p className="font-mono text-sm text-muted-foreground">Loading…</p>
        : !faqs.length ? <Empty label="No FAQs yet." />
        : (
        <div className="grid gap-px border border-border bg-border">
          {faqs.map((f) => (
            <div key={f.id} className="flex items-center justify-between gap-4 bg-background p-5" data-testid={`admin-faq-${f.id}`}>
              <div className="min-w-0">
                <h4 className="truncate font-medium text-foreground">{f.question}</h4>
                <p className="truncate text-sm text-muted-foreground">{f.answer}</p>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(f)} data-testid={`faq-edit-${f.id}`} className="text-muted-foreground hover:text-cyan"><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(f.id)} data-testid={`faq-delete-${f.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
